import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { useMasterData } from '../masterdata/queries';
import { useCalendars, useCreateSla, useDeleteSla, useSlas, useUpdateSla } from './queries';
import { Select } from '../../components/Select';

/**
 * Die Servicezusagen -- F4-02, K-115.
 *
 * Die Ziele werden je Prioritaet gepflegt, und zwar **alle auf einmal**: Der
 * Server ersetzt die Liste, statt sie zu ergaenzen. Eine Maske, die einzelne
 * Ziele nachreicht, taeuschte etwas anderes vor -- und ein Ziel, das
 * versehentlich stehen bleibt, faellt erst auf, wenn ein Ticket dieser
 * Prioritaet die falsche Frist bekommt.
 *
 * Eingegeben wird in **Minuten**. Stunden waeren bequemer zu lesen, aber eine
 * Reaktionszeit von dreissig Minuten liesse sich dann nur als 0,5 eintragen
 * -- und der erste, der 30 tippt, hat dreissig Stunden zugesagt.
 */

interface TargetRow {
  priority_id: string;
  first_response_minutes: string;
  resolution_minutes: string;
}

export function SlaPanel() {
  const { t } = useTranslation();
  const slas = useSlas();
  const calendars = useCalendars();
  const priorities = useMasterData('priority');
  const create = useCreateSla();
  const update = useUpdateSla();
  const remove = useDeleteSla();

  const [name, setName] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [warnAt, setWarnAt] = useState('80');
  const [editing, setEditing] = useState<string | null>(null);
  const [rows, setRows] = useState<TargetRow[]>([]);

  const priorityName = (id: string) =>
    priorities.data?.find((entry) => entry.id === id)?.name ?? id;

  const startEditing = (slaId: string) => {
    const sla = slas.data?.find((entry) => entry.id === slaId);
    setEditing(slaId);
    setRows(
      (priorities.data ?? []).map((priority) => {
        const target = sla?.targets?.find((entry) => entry.priority_id === priority.id);
        return {
          priority_id: priority.id,
          first_response_minutes: target?.first_response_minutes?.toString() ?? '',
          resolution_minutes: target?.resolution_minutes?.toString() ?? '',
        };
      }),
    );
  };

  const saveTargets = () => {
    if (editing === null) return;
    update.mutate(
      {
        id: editing,
        payload: {
          targets: rows
            .filter((row) => row.first_response_minutes || row.resolution_minutes)
            .map((row) => ({
              priority_id: row.priority_id,
              first_response_minutes: row.first_response_minutes
                ? Number(row.first_response_minutes)
                : null,
              resolution_minutes: row.resolution_minutes ? Number(row.resolution_minutes) : null,
            })),
        },
      },
      {
        onSuccess: () => {
          setEditing(null);
        },
      },
    );
  };

  return (
    <div className="stack">
      <section className="card">
        <h2>{t('sla.newSla')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="sla-name">
            {t('masterdata.name')}
          </label>
          <input
            id="sla-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="sla-calendar">
            {t('sla.calendar')}
          </label>
          <Select
            id="sla-calendar"
            className="field__control"
            value={calendarId}
            onChange={(event) => {
              setCalendarId(event.target.value);
            }}
          >
            <option value="">{t('sla.chooseCalendar')}</option>
            {(calendars.data ?? []).map((calendar) => (
              <option key={calendar.id} value={calendar.id}>
                {calendar.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="sla-warn">
            {t('sla.warnAtPercent')}
          </label>
          <input
            id="sla-warn"
            type="number"
            min={1}
            max={99}
            className="field__control"
            value={warnAt}
            onChange={(event) => {
              setWarnAt(event.target.value);
            }}
          />
          <span className="field__hint">{t('sla.warnAtPercentHint')}</span>
        </div>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!name.trim() || !calendarId}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              {
                name: name.trim(),
                business_calendar_id: calendarId,
                warn_at_percent: Number(warnAt) || 80,
              },
              {
                onSuccess: () => {
                  setName('');
                  setCalendarId('');
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>

      <section className="card card--flush">
        {slas.isPending ? <LoadingIndicator /> : null}
        {slas.isError ? <ErrorNotice error={slas.error} /> : null}

        {slas.data && slas.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('sla.warnAtPercent')}</th>
                  <th scope="col">{t('sla.targets')}</th>
                  <th scope="col">{t('masterdata.isDefault')}</th>
                  <th scope="col">{t('tickets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {slas.data.map((sla) => (
                  <tr key={sla.id}>
                    <td className="table__subject">{sla.name}</td>
                    <td className="table__muted">{sla.warn_at_percent} %</td>
                    <td className="table__muted">
                      {(sla.targets ?? []).length === 0
                        ? t('sla.noTargets')
                        : (sla.targets ?? [])
                            .map(
                              (target) =>
                                `${priorityName(target.priority_id)}: ` +
                                `${target.first_response_minutes ?? '–'}/${target.resolution_minutes ?? '–'}`,
                            )
                            .join(' · ')}
                    </td>
                    <td>
                      {sla.is_default ? (
                        <Badge tone="info">{t('masterdata.yes')}</Badge>
                      ) : (
                        <span className="table__muted">{t('masterdata.no')}</span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          startEditing(sla.id);
                        }}
                      >
                        {t('sla.editTargets')}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          remove.mutate(sla.id);
                        }}
                      >
                        {t('masterdata.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {remove.isError ? <ErrorNotice error={remove.error} /> : null}
      </section>

      {editing !== null ? (
        <section className="card">
          <h2>{t('sla.targets')}</h2>
          <p className="table__muted">{t('sla.targetsHint')}</p>

          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('tickets.priority')}</th>
                  <th scope="col">{t('sla.firstResponseMinutes')}</th>
                  <th scope="col">{t('sla.resolutionMinutes')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.priority_id}>
                    <td>{priorityName(row.priority_id)}</td>
                    <td>
                      <input
                        type="number"
                        min={1}
                        className="field__control"
                        aria-label={`${priorityName(row.priority_id)} ${t('sla.firstResponseMinutes')}`}
                        value={row.first_response_minutes}
                        onChange={(event) => {
                          setRows((current) =>
                            current.map((item, position) =>
                              position === index
                                ? { ...item, first_response_minutes: event.target.value }
                                : item,
                            ),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min={1}
                        className="field__control"
                        aria-label={`${priorityName(row.priority_id)} ${t('sla.resolutionMinutes')}`}
                        value={row.resolution_minutes}
                        onChange={(event) => {
                          setRows((current) =>
                            current.map((item, position) =>
                              position === index
                                ? { ...item, resolution_minutes: event.target.value }
                                : item,
                            ),
                          );
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {update.isError ? <ErrorNotice error={update.error} /> : null}

          <div className="row">
            <Button variant="primary" busy={update.isPending} onClick={saveTargets}>
              {t('app.save')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setEditing(null);
              }}
            >
              {t('app.cancel')}
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
